import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import ConfirmationPopup, {
  DIALOG_TYPES,
} from "../../../packages/ConfirmationPopup";
import WPDropdown from "../../../packages/WPDropdown";

import PassTabs from "./PassTabs";

import { CustomModalButtons } from "../../../constants/global.constants";

import { CheckCircleIcon } from "../../../assets/icons";

import CustomRadioCheck from "../../../common/styles/components/checkboxs/CustomRadioCheck";
import CustomTooltip from "../../../common/styles/components/tooltip/CustomTooltip";

const TrainConfigTab = ({ config, trainCaseRoMap, setTrainCaseRoMap }) => {
  const { t } = useTranslation();

  const [caseName, setCaseName] = useState(null);
  const [roName, setRoName] = useState(null);
  const [applyToAllTrains, setApplyToAllTrains] = useState(false);
  const [showApplyToAllConfirm, setShowApplyToAllConfirm] = useState(false);

  const totalCases = config?.totalCases || 0;
  const cases = useMemo(() => config?.cases || [], [config?.cases]);

  const caseOptions = useMemo(
    () => cases.map((item) => item?.caseName).filter(Boolean),
    [cases],
  );

  const selectedCase = useMemo(
    () => cases.find((item) => item?.caseName === caseName) || cases[0],
    [cases, caseName],
  );

  const selectedRODesign = useMemo(
    () =>
      (selectedCase?.roTreatments || []).find(
        (item) => item?.nodeId === roName,
      ) || selectedCase?.roTreatments?.[0],
    [selectedCase, roName],
  );

  const roOptions = useMemo(
    () =>
      (selectedCase?.roTreatments || [])
        .map((item) => item?.nodeId)
        .filter(Boolean),
    [selectedCase],
  );

  useEffect(() => {
    if (!caseName && caseOptions.length > 0) {
      setCaseName(caseOptions[0]);
    }
  }, [caseOptions, caseName]);

  useEffect(() => {
    if (roOptions.length === 0) {
      setRoName("");
      return;
    }

    if (!roOptions.includes(roName)) {
      setRoName(roOptions[0]);
    }
  }, [roOptions, roName]);

  const getDropdownOptions = (optionsArr) => {
    return optionsArr.map((option) => ({
      value: option,
      label: option,
    }));
  };

  const handleDropdownChange = (setter) => (selectedOption) => {
    setter(selectedOption?.value || "");
  };

  const areSelectionsMatch = (left, right) => {
    if (!left || !right) {
      return false;
    }
    return left.caseName === right.caseName && left.roName === right.roName;
  };

  const getTrainSelectionDisplayValue = (selection) => {
    return `\n CASE: ${selection.caseName} \n\n RO: ${selection.roName}`;
  };

  const selectedTrainConfig = {
    caseName: caseName || "",
    roName: roName || "",
  };

  const areAllTrainsSelectedForCurrentConfig = useMemo(() => {
    const trainValues = Object.values(trainCaseRoMap || {});
    if (trainValues.length === 0 || !caseName || !roName) {
      return false;
    }

    return trainValues.every((value) =>
      areSelectionsMatch(value, selectedTrainConfig),
    );
  }, [trainCaseRoMap, caseName, roName, selectedTrainConfig]);

  useEffect(() => {
    setApplyToAllTrains(areAllTrainsSelectedForCurrentConfig);
  }, [areAllTrainsSelectedForCurrentConfig]);

  const handleTrainSelectionChange = (trainNumber, isChecked) => {
    setTrainCaseRoMap((prevSelection) => {
      const newSelection = { ...prevSelection };
      newSelection[`train${trainNumber}`] = isChecked
        ? selectedTrainConfig
        : null;
      return newSelection;
    });
  };

  const shouldDisableTrainSelection = (trainNumber) => {
    const trainValue = trainCaseRoMap[`train${trainNumber}`];
    return trainValue && !areSelectionsMatch(trainValue, selectedTrainConfig);
  };

  const handleApplytoAllTrainsChange = (event) => {
    const isChecked = event.target.checked;

    if (isChecked) {
      setShowApplyToAllConfirm(true);
      return;
    }

    setTrainCaseRoMap((prevSelection) => {
      const newSelection = { ...prevSelection };
      Object.keys(newSelection).forEach((trainKey) => {
        newSelection[trainKey] = null;
      });
      return newSelection;
    });
  };

  const handleApplyToAllConfirmClose = (action) => {
    setShowApplyToAllConfirm(false);

    if (action !== CustomModalButtons.CONFIRM) {
      return;
    }

    setTrainCaseRoMap((prevSelection) => {
      const newSelection = { ...prevSelection };
      Object.keys(newSelection).forEach((trainKey) => {
        newSelection[trainKey] = selectedTrainConfig;
      });
      return newSelection;
    });
  };

  return (
    <>
      <section className="wavepro-preview-dropdown-section">
        <div className="wavepro-preview-dropdown-grid">
          <WPDropdown
            label={t("importPlantWavePro.selectCase")}
            id={"case-dropdown"}
            value={caseName}
            onChange={handleDropdownChange(setCaseName)}
            options={getDropdownOptions(caseOptions)}
            required
          />
          <WPDropdown
            label={t("importPlantWavePro.selectROConfig")}
            id={"roName-dropdown"}
            value={roName}
            onChange={handleDropdownChange(setRoName)}
            options={getDropdownOptions(roOptions)}
            required
          />
        </div>

        <div className="cases-message-container">
          <CheckCircleIcon className="cases-check-icon" />
          <span>
            {t("importPlantWavePro.totalCasesMessage", {
              roCases: cases.length,
              totalCases,
            })}
          </span>
        </div>
      </section>

      <PassTabs selectedRODesign={selectedRODesign} />

      {selectedCase?.caseNote && (
        <div className="case-notes">
          <strong>{t("importPlantWavePro.caseNotes")}:</strong>{" "}
          {selectedCase?.caseNote}
        </div>
      )}

      <div className="ro-card-wrapper apply-train-config-section">
        <div className="train-config-header">
          <div className="header-text">
            {t("importPlantWavePro.applyToTrains")}:
          </div>
          <CustomRadioCheck
            id={`apply-all-train-checkbox`}
            label={t("importPlantWavePro.sameConfigForAllTrains")}
            checked={applyToAllTrains}
            onChange={handleApplytoAllTrainsChange}
          />
        </div>
        <div className="train-config-checkboxes">
          {Object.keys(trainCaseRoMap).map((trainKey, index) => (
            <CustomTooltip
              text={
                shouldDisableTrainSelection(index + 1)
                  ? t("importPlantWavePro.trainConfigSelected", {
                      config: getTrainSelectionDisplayValue(
                        trainCaseRoMap[`train${index + 1}`],
                      ),
                    })
                  : ""
              }
              key={trainKey}
              maxWidth="160px"
            >
              <span>
                <CustomRadioCheck
                  id={`train-checkbox-${index}`}
                  label={`${t("ValueStrings.train")} ${index + 1}`}
                  checked={Boolean(trainCaseRoMap[`train${index + 1}`])}
                  onChange={(event) =>
                    handleTrainSelectionChange(index + 1, event.target.checked)
                  }
                  disabled={shouldDisableTrainSelection(index + 1)}
                />
              </span>
            </CustomTooltip>
          ))}
        </div>
      </div>

      {showApplyToAllConfirm && (
        <ConfirmationPopup
          type={DIALOG_TYPES.WARNING}
          header={t("importPlantWavePro.applyAllTrainsConfirmHeader")}
          description={t("importPlantWavePro.applyAllTrainsConfirmDescription")}
          confirmBtn={t("importPlantWavePro.applyAllTrainsConfirmBtn")}
          cancelBtn={t("ValueStrings.cancel")}
          onClose={handleApplyToAllConfirmClose}
        />
      )}
    </>
  );
};

export default TrainConfigTab;
